import React, { useState } from 'react'
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  Box,
  TextField,
  CardContent,
  Button,
  LinearProgress,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from '@mui/material'
import { Card } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import {
  getCollectorAnalysisCollectorSheetReportRequest,
  getCollectorAnalysisZonesReportRequest,
} from '../../Utils/RestUtils'
import { useGetList } from 'react-admin'
import InputAdornment from '@mui/material/InputAdornment'
import CollectorDetails from './CollectorDetails'

export default function CollectorAnalysis() {
  const [reportResponse, setReportResponse] = useState(null)
  const [collectorSheetResponse, setCollectorSheetResponse] = useState(null)
  const [selectedZone, setSelectedZone] = useState(null)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const [filters, setFilters] = useState({
    from: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
    to: new Date().toISOString(),
    collectionAgentId: 80,
  })

  const { data: collectionAgents, isLoading } = useGetList(
    'collection-agents',
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: 'collectionAgentCompanyName', order: 'ASC' },
      filter: { collectionAgentContactActive: 1 },
    }
  )

  const handleFilterChange = (event) => {
    setFilters({ ...filters, [event.target.name]: event.target.value })
  }

  const getCancellationRate = (zone) => {
    if (zone.quotes_canceled_count + zone.quotes_collected_count !== 0) {
      return (
        (zone.quotes_canceled_count /
          (zone.quotes_canceled_count + zone.quotes_collected_count)) *
        100
      ).toFixed(2)
    }

    return 0
  }

  const getCollectorAnalysisReport = async () => {
    setIsLoadingReport(true)
    const response = await getCollectorAnalysisZonesReportRequest(filters)
    setReportResponse(response.data)
    setIsLoadingReport(false)
  }

  return (
    <CardContent>
      <Typography typography="h4" component="h1" mb={2}>
        Collector Analysis
      </Typography>
      <Box
        sx={{
          display: 'flex',
          marginTop: '10px',
          marginBottom: '10px',
          gap: '1rem',
        }}
      >
        <Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              inputFormat="DD/MM/YYYY"
              label="From"
              value={dayjs(filters.from)}
              onChange={(selectedDate) =>
                setFilters({
                  ...filters,
                  from: selectedDate.toDate().toISOString(),
                })
              }
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </Box>
        <Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              inputFormat="DD/MM/YYYY"
              // minDate={fromDate}
              maxDate={
                new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
              }
              label="To"
              value={dayjs(filters.to)}
              onChange={(selectedDate) =>
                setFilters({
                  ...filters,
                  to: selectedDate.toDate().toISOString(),
                })
              }
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </Box>
        <Box>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="collection-agent-label">
              Collection Agent
            </InputLabel>
            <Select
              name="collectionAgentId"
              labelId="collection-agent-label"
              value={filters.collectionAgentId}
              label="Collection Agent"
              onChange={handleFilterChange}
              disabled={isLoading}
              endAdornment={
                isLoading && (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                )
              }
            >
              {collectionAgents?.map((agent) => (
                <MenuItem key={agent.id} value={agent.originId}>
                  {agent.collectionAgentCompanyName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box>
          <Button
            disabled={isLoading}
            onClick={() => {
              getCollectorAnalysisReport()
            }}
          >
            Search
          </Button>
        </Box>
      </Box>
      <Card style={{ padding: '20px' }}>
        {isLoadingReport ? (
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        ) : (
          reportResponse !== null &&
          reportResponse.zones.length !== 0 && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '15%' }}>
                    Collector name/zone
                  </TableCell>
                  <TableCell>Collector dominance</TableCell>
                  <TableCell>Average price per ton</TableCell>
                  <TableCell>Postcodes covered</TableCell>
                  <TableCell>Quotes in zone</TableCell>
                  <TableCell>Assigned</TableCell>
                  <TableCell>Collected</TableCell>
                  <TableCell>Cancelled</TableCell>
                  <TableCell>Cancellation rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportResponse?.zones.map((zone) => (
                  <TableRow key={zone.name}>
                    <TableCell
                      onClick={async () => {
                        setSelectedZone(zone)
                        setCollectorSheetResponse(
                          (
                            await getCollectorAnalysisCollectorSheetReportRequest(
                              {
                                collectionAgentId: zone.collection_agent_id,
                                postcodes: reportResponse.postcode_coverage,
                              }
                            )
                          )?.data
                        )
                      }}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                          color: 'primary.main',
                          fontWeight: 'bold',
                        },
                      }}
                    >
                      {zone.company_name} / {zone.zone_name}
                    </TableCell>
                    <TableCell>{zone.perc_max_prices}%</TableCell>
                    <TableCell>£{zone.average_price_per_tone}</TableCell>
                    <TableCell>{zone.postcodes_covered_count}</TableCell>
                    <TableCell>{zone.total_quotes_in_zone}</TableCell>
                    <TableCell>
                      {zone.quotes_collected_count + zone.quotes_canceled_count}
                    </TableCell>
                    <TableCell>{zone.quotes_collected_count}</TableCell>
                    <TableCell>{zone.quotes_canceled_count}</TableCell>
                    <TableCell>{getCancellationRate(zone)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        )}
        {reportResponse && reportResponse.zones.length === 0 ? (
          <Alert icon={false} severity="error">
            No results found.
          </Alert>
        ) : null}
        {reportResponse === null && (
          <Alert icon={false} severity="info">
            Search for results
          </Alert>
        )}
        {selectedZone !== null && (
          <CollectorDetails
            selectedZone={selectedZone}
            zonesReportResponse={reportResponse}
            collectorSheetResponse={collectorSheetResponse}
          ></CollectorDetails>
        )}
      </Card>
    </CardContent>
  )
}
